/**
 * «Kun for medlemmer» + «Tæller ikke med i medlemskab» (fx Halgodts Saunagus: kun
 * medlemmer må booke, men alle betaler drop-in 150 kr). ÉN regel for alle HS'
 * booking-flader: den native booking-modal, CMS-widgetten, embed-widgetten
 * (packages/widgets) og widgettens booking-API.
 *
 * ⚠️ Filen findes to steder med IDENTISK indhold under denne kommentar:
 *   apps/holdstyring/src/lib/bookings/members-only-dropin.ts
 *   packages/widgets/src/core/members-only.ts
 * Widget-bundlet kan ikke importere fra appen (eget Vite-build uden workspace-pakker).
 * `members-only-dropin.test.ts` i HS kører samme sandhedstabel mod begge og fejler,
 * hvis de driver fra hinanden. Ret begge.
 *
 * Ren modul uden afhængigheder.
 */

export interface MembersOnlyFlags {
  membershipRequired?: boolean | null
  excludeFromMembership?: boolean | null
}

/** Kun medlemmer må booke, men medlemskabet dækker aldrig holdet: der betales drop-in. */
export function isMembersOnlyDropIn(cls: MembersOnlyFlags): boolean {
  return cls.membershipRequired === true && cls.excludeFromMembership === true
}

/**
 * Adgangen til at booke holdet.
 *  - 'open': alle må booke (intet medlemskabskrav).
 *  - 'allowed' / 'blocked': members-only drop-in; et aktivt medlemskab giver adgang
 *    (og der betales drop-in), uden et er der ingen vej ind.
 *  - 'needs_coverage': almindeligt members-only-hold; et medlemskab skal DÆKKE holdet.
 *    Afgøres af dækningen (abonnement/klippekort), aldrig af drop-in-betaling.
 */
export type MembersOnlyAccess = 'open' | 'allowed' | 'blocked' | 'needs_coverage'

export function membersOnlyAccess(cls: MembersOnlyFlags, hasActiveMembership: boolean): MembersOnlyAccess {
  if (cls.membershipRequired !== true) return 'open'
  if (cls.excludeFromMembership === true) return hasActiveMembership ? 'allowed' : 'blocked'
  return 'needs_coverage'
}

/** Kan en booking af holdet betales som drop-in? Aldrig på et almindeligt members-only-hold. */
export function paysDropIn(cls: MembersOnlyFlags & { dropInPriceDkk?: number | null }): boolean {
  if (!((cls.dropInPriceDkk ?? 0) > 0)) return false
  return cls.membershipRequired !== true || cls.excludeFromMembership === true
}

/**
 * Hvad holdkortet skal vise som pris: drop-in-prisen, «Kun medlemmer» eller «Gratis».
 * Et members-only drop-in-hold viser prisen (det er den, medlemmet betaler), med
 * `membersOnly: true`, så fladen kan tilføje «kun medlemmer».
 */
export function classPriceDisplay(
  cls: MembersOnlyFlags & { dropInPriceDkk?: number | null },
): { kind: 'price' | 'membersOnly' | 'free'; membersOnly: boolean } {
  const membersOnly = cls.membershipRequired === true
  if (paysDropIn(cls)) return { kind: 'price', membersOnly }
  if (membersOnly) return { kind: 'membersOnly', membersOnly }
  return { kind: 'free', membersOnly }
}
